import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { loadDocument, type LoadedDocument } from '../lib/document';

export type LibraryItem = {
  id: string;
  title: string;
  size: number;
  uploaded_at: number;
  pageCount?: number;
};

export function buildRasterizedDoc(item: LibraryItem): LoadedDocument {
  const pageCount = item.pageCount ?? 0;
  return {
    kind: 'rasterized-pdf',
    pageCount,
    pageUrls: Array.from({ length: pageCount }, (_, i) =>
      `/library/${item.id}/page-${String(i + 1).padStart(3, '0')}.png`
    ),
    fileHash: item.id,
    fileName: item.title,
    transient: true,
  };
}

type Props = {
  onClose: () => void;
  onPick: (doc: LoadedDocument) => void;
};

export function LibraryPicker({ onClose, onPick }: Props) {
  const t = useT();
  const [items, setItems] = useState<LibraryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchingId, setFetchingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/library/catalog.json', { cache: 'no-store' });
        if (!res.ok) {
          if (res.status === 404) {
            if (!cancelled) setItems([]);
            return;
          }
          throw new Error('http ' + res.status);
        }
        const data = await res.json();
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message || 'fetch failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function pickItem(item: LibraryItem) {
    setError(null);
    setFetchingId(item.id);
    try {
      if (item.pageCount && item.pageCount > 0) {
        // Растеризованный путь — отдаём массив URL'ов PNG страниц
        onPick(buildRasterizedDoc(item));
        return;
      }
      // Fallback (старые файлы без растеризации): pdf.js через blob
      const res = await fetch(`/library/${item.id}.pdf`, { cache: 'no-store' });
      if (!res.ok) throw new Error('http ' + res.status);
      const blob = await res.blob();
      const file = new File([blob], item.title.endsWith('.pdf') ? item.title : item.title + '.pdf', {
        type: 'application/pdf',
      });
      const doc = await loadDocument(file);
      onPick({ ...doc, transient: true });
    } catch (e) {
      setError((e as Error).message || 'fetch failed');
      setFetchingId(null);
    }
  }

  function fmtSize(bytes: number): string {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  }

  function fmtDate(ts: number): string {
    const d = new Date(ts * 1000);
    return d.toLocaleDateString();
  }

  return (
    <div className="lib-backdrop" onClick={onClose}>
      <div className="lib-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lib-header">
          <h2>{t('picker.libraryTitle')}</h2>
          <button onClick={onClose}>{t('picker.libraryClose')}</button>
        </div>
        <div className="lib-body">
          {items === null && !error && <div className="lib-empty">{t('picker.libraryLoading')}</div>}
          {error && <div className="lib-error">{t('picker.libraryError')}: {error}</div>}
          {items && items.length === 0 && !error && (
            <div className="lib-empty">{t('picker.libraryEmpty')}</div>
          )}
          {items && items.length > 0 && (
            <ul className="lib-list">
              {items.map((it) => (
                <li key={it.id}>
                  <button
                    className="lib-item"
                    onClick={() => pickItem(it)}
                    disabled={fetchingId !== null}
                  >
                    <span className="lib-item-title">{it.title}</span>
                    <span className="lib-item-meta">
                      {fmtSize(it.size)} · {fmtDate(it.uploaded_at)}
                    </span>
                    {fetchingId === it.id && <span className="lib-item-loading">…</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
